import { getCombinedWeeklyCallLogs, getCombinedWeeklyTextLogs } from '@/api/logs';
import { removeInboundSeat, removeOutboundSeat, deleteAgent } from '@/api/user';
import {
	getInboundAgents,
	getLoggedInUser,
	getOutboundAgents,
	getSelectedAgent,
	getcurrentPage,
} from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import {
	DollarCircleOutlined,
	GlobalOutlined,
	InfoCircleOutlined,
	MessageOutlined,
	PhoneOutlined,
	SoundOutlined,
	PlusOutlined,
	UserAddOutlined,
	LockOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button, Tooltip, Divider, Modal, notification } from 'antd';
import { ReceptionistDashboardSiderItem } from 'src/enums/EReceptionistDashboardSiderItem';
import { StatCard } from './StatsCard';
import { Card } from './Card';
import { ActivityItem } from './ActivityItem';
import { renderPSTDate } from '@/utils/helper';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { setCurrentPage, setSelectedAgent, setSelectedAgentId } from '@/store/account/slice';
import { useLocalTimezone } from 'src/common/hooks/useLocalTimezone';
import CsmCard from './CsmCard';
import { CSM_MEMBERS } from 'src/constants';
import OutboundAgentCard from './OutboundAgentCard';
import InboundAgentCard from './InboundAgentCard';
import { MODAL_IDS } from 'src/enums/EModal';
import { openModal } from '@/store/modal/slice';
import InboundPlansModal from '@/components/PlansModal/PlansModal';
import OutboundPlansModal from '@/pages/Novi/components/NoviPlansModal';
import CampaignCreationModal from '@/pages/Novi/pages/Dashboard/Campagins/AICampaign/CampaignCreationModal';
import { useEffect, useState } from 'react';
import { User } from '@/types/TAccounts';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'src/enums/ERoutes';
import AddAgentSubscriptionModal from './AddAgentSubscriptionModal';
import InboundAgentCreationModal from './InboundAgentCreationModal';
import UnconfiguredAgentModal from './UnconfiguredAgentModal';
import UnconfiguredAgentCard from './UnconfiguredAgentCard';
import useAuth from '@/routes/hooks/useAuth';
import { useAgents } from 'src/Hooks/useAgents';
import FloatingAccountBubble from '@/components/FloatingAccountBubble';

interface ActivityItemData {
	type: 'call' | 'text';
	title: string;
	subtitle: string;
	time: string;
	date: Date;
}

const Dashboard = () => {
	const dispatch = useAppDispatch();
	const loggedInUser = useAppSelector(getLoggedInUser);
	const selectedAgent = useAppSelector(getSelectedAgent);
	const { timeZone } = useLocalTimezone(selectedAgent?.time_zone);
	const inboundAgents = useAppSelector(getInboundAgents);
	const outboundAgents = useAppSelector(getOutboundAgents);
	const [isCampaignCreationModalOpen, setIsCampaignCreationModalOpen] = useState(false);
	const [isInboundAgentCreationModalOpen, setIsInboundAgentCreationModalOpen] = useState(false);
	const [deletingAgentId, setDeletingAgentId] = useState<string | null>(null);
	const currentPage = useAppSelector(getcurrentPage);
	const { refetch: refetchUser } = useAuth();
	const navigate = useNavigate();
	const { refetchAgents, isAgentsLoading } = useAgents();

	// Add useEffect to handle returning to dashboard
	useEffect(() => {
		if (currentPage === ReceptionistDashboardSiderItem.DASHBOARD && !selectedAgent) {
			// Reset any necessary state when returning to dashboard
			dispatch(setSelectedAgentId({ selectedAgentId: null }));
		}
	}, [currentPage, selectedAgent, dispatch]);

	const { data: callLogsData, isPending: isCallLogsPending } = useQuery({
		queryKey: ['getWeeklyCallLogs'],
		queryFn: () => {
			return getCombinedWeeklyCallLogs({
				limit: null,
				startDate: new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 7).getTime(), // get past week record
			});
		},
	});
	const { data: completeTextLogsData, isPending: isTextLogsPending } = useQuery({
		queryKey: ['getTextLogs'],
		queryFn: () => getCombinedWeeklyTextLogs(),
	});

	const csmMember =
		CSM_MEMBERS.find((member) => member.email === loggedInUser?.csm) || CSM_MEMBERS[0];

	const textLogsData = {
		textLogs: completeTextLogsData?.data?.filter((text) => {
			const textDate = new Date(text.date);
			const oneWeekAgo = new Date();
			oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
			return textDate >= oneWeekAgo;
		}),
	};

	const generateChartData = () => {
		const callLogs = callLogsData?.data || [];
		const textLogs = textLogsData?.textLogs || [];

		const formatDate = (date: Date) => {
			return `${date.toLocaleString('default', { month: 'short' })} ${date.getDate()}`;
		};

		const generateDataForLogs = (logs: Array<{ date: string }>) => {
			if (logs.length === 0) {
				return {
					data: [],
					categories: [],
				};
			}

			const today = new Date();
			today.setHours(0, 0, 0, 0);

			const sevenDaysAgo = new Date(today);
			sevenDaysAgo.setDate(today.getDate() - 6);

			const dates = Array.from({ length: 7 }, (_, i) => {
				const date = new Date(sevenDaysAgo);
				date.setDate(sevenDaysAgo.getDate() + i);
				return date;
			});

			const dateLabels = dates.map(formatDate);

			const dateMap = new Map();
			dates.forEach((date, index) => {
				const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
				dateMap.set(dateString, index);
			});

			const countsByDate = Array(7).fill(0);

			logs.forEach((log) => {
				const logDate = new Date(log.date);
				const logDateString = `${logDate.getFullYear()}-${String(logDate.getMonth() + 1).padStart(2, '0')}-${String(logDate.getDate()).padStart(2, '0')}`;

				const index = dateMap.get(logDateString);
				if (index !== undefined) {
					countsByDate[index]++;
				}
			});

			return {
				data: countsByDate,
				categories: dateLabels,
			};
		};

		const callsChartData = generateDataForLogs(callLogs);
		const textsChartData = generateDataForLogs(textLogs);

		const revenueData = callsChartData.data.map((calls) => calls * 25);

		return {
			callsData: callsChartData.data,
			callsCategories: callsChartData.categories,
			textsData: textsChartData.data,
			textsCategories: textsChartData.categories,
			revenueData,
			revenueCategories: callsChartData.categories,
		};
	};

	const chartData = generateChartData();

	const combinedActivity: ActivityItemData[] = [
		...(callLogsData?.data || []).map((call) => ({
			type: 'call' as const,
			title: `Phone call ${call.from === selectedAgent?.provisionedNumber ? 'to' : 'from'} ${call.name || call.username || 'Unknown'}`,
			subtitle: `${call.from === selectedAgent?.provisionedNumber ? call.to : call.from} • ${call.callSummary?.length ? (call.callSummary?.length > 20 ? call.callSummary?.substring(0, 50) + '...' : call.callSummary) : 'No summary available'}`,
			time: renderPSTDate(call.date, timeZone),
			date: new Date(call.startedTimestamp ? call.startedTimestamp / 1000 : call.date),
		})),
		...(textLogsData?.textLogs || []).map((text) => ({
			type: 'text' as const,
			title: `Text message ${text.to === selectedAgent?.provisionedNumber ? 'received' : 'sent'}`,
			subtitle: `${text.to} • ${text.text?.substring(0, 20) || 'No content available'}${text.text && text.text.length > 20 ? '...' : ''}`,
			time: renderPSTDate(text.date, timeZone),
			date: new Date(text.startedTimestamp ? text.startedTimestamp / 1000 : text.date),
		})),
	];

	const recentActivity =
		!isCallLogsPending && !isTextLogsPending
			? combinedActivity.sort((a, b) => b.date.getTime() - a.date.getTime()).slice(0, 3)
			: [];

	const [addAgentModalOpen, setAddAgentModalOpen] = useState<null | 'inbound' | 'outbound'>(null);
	const [unconfiguredAgentModalOpen, setUnconfiguredAgentModalOpen] = useState<
		null | 'inbound' | 'outbound'
	>(null);

	// Handlers for new buttons
	const handleSubscribeNow = (suite: 'inbound' | 'outbound') => {
		if (suite === 'inbound') {
			dispatch(
				openModal({
					modalId: MODAL_IDS.PLANS_MODAL,
				}),
			);
		} else {
			dispatch(openModal({ modalId: MODAL_IDS.NOVI_PLANS_MODAL }));
		}
	};

	const handleCreateDemoAccount = (suite: 'inbound' | 'outbound') => {
		if (suite === 'inbound') {
			setIsInboundAgentCreationModalOpen(true);
		} else {
			setIsCampaignCreationModalOpen(true);
		}
	};

	const handleBuyNewAgent = (suite: 'inbound' | 'outbound') => {
		const unconfiguredCount =
			suite === 'inbound'
				? loggedInUser?.unConfiguredInboundAgents || 0
				: loggedInUser?.unConfiguredOutboundAgents || 0;

		if (unconfiguredCount > 0) {
			setUnconfiguredAgentModalOpen(suite);
		} else {
			setAddAgentModalOpen(suite);
		}
	};

	const handleConfigureAgent = (type: 'inbound' | 'outbound') => {
		if (type === 'inbound') {
			setIsInboundAgentCreationModalOpen(true);
		} else {
			setIsCampaignCreationModalOpen(true);
		}
	};

	const handleDeleteUnconfiguredAgent = (type: 'inbound' | 'outbound'): void => {
		const configuredCount =
			type === 'inbound'
				? loggedInUser?.configuredInboundAgents || 0
				: loggedInUser?.configuredOutboundAgents || 0;
		const unconfiguredCount =
			type === 'inbound'
				? loggedInUser?.unConfiguredInboundAgents || 0
				: loggedInUser?.unConfiguredOutboundAgents || 0;

		const totalAgents = configuredCount + unconfiguredCount;

		if (totalAgents <= 1) {
			Modal.confirm({
				title: 'Cannot Delete Last Agent',
				content: `You must maintain at least 1 ${type} agent at all times. If you wish to remove all agents, please cancel your subscription instead.`,
				okText: 'Cancel Subscription',
				okType: 'danger',
				cancelText: 'Close',
				onOk: () => {
					// Navigate to subscription management page
					dispatch(setCurrentPage(ReceptionistDashboardSiderItem.SUBSCRIPTION_MANAGEMENT));
				},
			});
			return;
		}

		Modal.confirm({
			title: 'Delete Unconfigured Agent',
			content: `Are you sure you want to delete this unconfigured ${type} agent? This action cannot be undone.`,
			okText: 'Delete',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk: () => {
				if (type === 'inbound') {
					removeInboundSeatMutation();
				} else {
					removeOutboundSeatMutation();
				}
			},
		});
	};

	const { mutate: deleteAgentMutation } = useMutation({
		mutationFn: deleteAgent,
		onSuccess: () => {
			setDeletingAgentId(null);
			notification.success({
				message: 'Agent Deleted',
				description: 'The agent has been successfully deleted.',
				placement: 'topRight',
			});
			refetchAgents();
			refetchUser();
		},
		onError: (error) => {
			setDeletingAgentId(null);
			notification.error({
				message: 'Failed to Delete Agent',
				description: error?.message || 'There was an error deleting the agent. Please try again.',
				placement: 'topRight',
			});
		},
	});

	const { mutate: removeInboundSeatMutation, isPending: isRemovingInbound } = useMutation({
		mutationFn: removeInboundSeat,
		onSuccess: () => {
			notification.success({
				message: 'Agent Deleted',
				description: 'Successfully deleted un-configured inbound agent.',
				placement: 'topRight',
			});
			refetchAgents();
			refetchUser();
		},
		onError: () => {
			notification.error({
				message: 'Failed to Delete Agent',
				description: 'There was an error deleting the agent. Please try again.',
				placement: 'topRight',
			});
		},
	});

	const { mutate: removeOutboundSeatMutation, isPending: isRemovingOutbound } = useMutation({
		mutationFn: removeOutboundSeat,
		onSuccess: () => {
			notification.success({
				message: 'Agent Deleted',
				description: 'Successfully deleted un-configured outbound agent.',
				placement: 'topRight',
			});
			refetchAgents();
			refetchUser();
		},
		onError: () => {
			notification.error({
				message: 'Failed to Delete Agent',
				description: 'There was an error deleting the agent. Please try again.',
				placement: 'topRight',
			});
		},
	});

	const handleDeleteAgent = (agent: User, type: 'inbound' | 'outbound') => {
		const configuredCount =
			type === 'inbound'
				? loggedInUser?.configuredInboundAgents || 0
				: loggedInUser?.configuredOutboundAgents || 0;

		const totalAgents = configuredCount;

		if (totalAgents <= 1) {
			notification.info({
				message: `You must maintain at least 1 ${type} receptionist all the time.`,
			});
			return;
		}
		Modal.confirm({
			title: 'Delete Agent',
			content: `Are you sure you want to delete ${agent.number}? This action cannot be undone.`,
			okText: 'Delete',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk: () => {
				setDeletingAgentId(agent.number);
				deleteAgentMutation({ agentId: agent.number, clientType: 'Client' });
			},
		});
	};

	const unconfiguredInboundCount = (loggedInUser?.unConfiguredInboundAgents || 0) as number;
	const unconfiguredOutboundCount = (loggedInUser?.unConfiguredOutboundAgents || 0) as number;

	return (
		<div className="pb-8 px-6">
			<div className="flex justify-between items-center mb-8">
				<h1 className="text-2xl font-bold">Dashboard</h1>

				<div className="block w-full md:w-auto !bg-gray-100 !rounded-2xl shadow-sm p-4 sm:p-6 cursor-pointer hover:bg-gray-200 transition-all duration-300 border border-gray-200">
					<FloatingAccountBubble debrand={loggedInUser?.resellerClient} />
				</div>
			</div>

			<div className="flex flex-col xl:grid xl:grid-cols-4 gap-6 mb-10">
				<div className="col-span-3">
					{/* Stats Row */}
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
						<StatCard
							icon={<PhoneOutlined rotate={90} />}
							title="Calls Answered (Last 7 days)"
							data={chartData.callsData}
							category={chartData.callsCategories}
							value={callLogsData?.data?.length || 0}
							color="#4361EE"
							isLoading={isCallLogsPending}
						/>
						<StatCard
							icon={<MessageOutlined />}
							title="Texts Sent (Last 7 days)"
							data={chartData.textsData}
							category={chartData.textsCategories}
							value={textLogsData?.textLogs?.length || 0}
							color="#10B981"
							isLoading={isTextLogsPending}
						/>
						<StatCard
							icon={<DollarCircleOutlined />}
							title={
								<div className="flex items-center gap-2">
									<span>Revenue Impact (Last 7 days)</span>
									<Tooltip title="On average, each call answered tends to generate $25 of revenue">
										<InfoCircleOutlined className="w-3 h-3" />
									</Tooltip>
								</div>
							}
							data={chartData.revenueData}
							category={chartData.revenueCategories}
							value={`$${(callLogsData?.data?.length || 0) * 25}`}
							color="#F59E0B"
							isLoading={isCallLogsPending}
						/>
					</div>

					<div className="mt-10 mb-6">
						<h2 className="text-xl font-bold m-0 mb-5">My AI Agents</h2>
					</div>

					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
						<Card>
							<div className="p-4">
								<div className="flex items-center pb-4 mb-5 border-b border-gray-200">
									<div className="flex items-center justify-center w-9 h-9 rounded-lg text-base mr-4 bg-blue-50 text-blue-600">
										<PhoneOutlined rotate={180} />
									</div>
									<h3 className="text-lg font-semibold text-gray-800 m-0">Inbound Suite</h3>
								</div>

								{isAgentsLoading ? (
									<div className="flex justify-center items-center py-8">
										<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
									</div>
								) : (
									<>
										{(loggedInUser.inboundDemo ||
											loggedInUser.inboundSubscriptionStatus === 'canceled') &&
											inboundAgents?.length >= 1 && (
												<div className="bg-blue-50 border border-blue-100 rounded-lg p-4 my-4 flex flex-col items-center">
													<div className="text-blue-700 text-base font-semibold mb-2">
														No active inbound subscription
													</div>
													<div className="text-blue-500 text-sm mb-4 text-center">
														You are currently using a demo account. Please subscribe to unlock all
														inbound agent features!
													</div>
													<Button
														type="primary"
														icon={<PlusOutlined />}
														size="large"
														className="w-full max-w-xs bg-blue-600 hover:bg-blue-700 shadow"
														onClick={() => handleSubscribeNow('inbound')}
													>
														Subscribe Now
													</Button>
												</div>
											)}

										{inboundAgents.length === 0 ? (
											loggedInUser.inboundSubscriptionId &&
											loggedInUser.inboundSubscriptionStatus !== 'canceled' ? (
												<div className="text-center py-8 flex flex-col items-center justify-center">
													<PhoneOutlined className="text-4xl text-gray-400 mb-3" />
													<h3 className="text-lg font-medium text-gray-800 mb-2">
														No Inbound Agents
													</h3>
													<p className="text-gray-600 mb-4">
														Create your first inbound receptionist to get started
													</p>
													<Button
														type="primary"
														onClick={() => handleBuyNewAgent('inbound')}
														className="bg-green-600 hover:bg-green-700 w-[250px]"
														icon={<PlusOutlined />}
														size="large"
													>
														Create New Receptionist
													</Button>
												</div>
											) : (
												<div className="text-center py-8 flex flex-col items-center justify-center">
													<PhoneOutlined className="text-4xl text-gray-400 mb-3" />
													<h3 className="text-lg font-medium text-gray-800 mb-2">
														No Inbound Subscription
													</h3>
													<p className="text-gray-600 mb-4">
														Subscribe to Inbound plan to access inbound agent features
													</p>
													<div className=" flex flex-row gap-2 items-center">
														<Button
															type="primary"
															onClick={() => handleSubscribeNow('inbound')}
															className="bg-blue-600 hover:bg-blue-700 mb-2"
															icon={<PlusOutlined />}
														>
															Subscribe Now
														</Button>
														{loggedInUser.outboundSubscriptionStatus !== 'canceled' && (
															<Button
																type="default"
																onClick={() => handleCreateDemoAccount('inbound')}
																icon={<UserAddOutlined />}
															>
																Create Demo Account
															</Button>
														)}
													</div>
												</div>
											)
										) : (
											<>
												{inboundAgents.map((agent: User, index: number) => (
													<div key={index} className="mb-4">
														<InboundAgentCard
															icon={<PhoneOutlined rotate={0} />}
															name={agent.name}
															status={
																loggedInUser?.inboundSubscriptionId &&
																loggedInUser?.inboundSubscriptionStatus !== 'canceled'
																	? 'active'
																	: 'demo'
															}
															onExpand={() => {
																const elem = document.getElementById(`inbound-nested-${index}`);
																if (elem) {
																	elem.style.display =
																		elem.style.display === 'none' ? 'block' : 'none';
																}
															}}
															isExpandable={true}
															onOpen={() => {
																dispatch(setSelectedAgentId({ selectedAgentId: agent.number }));
																dispatch(setSelectedAgent({ user: agent }));
																navigate(
																	AppRoutes.INBOUND_AGENT_PORTAL.replace(':agentId', agent.number),
																);
															}}
															onDelete={() => handleDeleteAgent(agent, 'inbound')}
															loading={deletingAgentId === agent.number}
															disabled={isRemovingInbound || isRemovingOutbound}
														/>
														<div
															id={`inbound-nested-${index}`}
															className="py-2"
															style={{ display: 'block' }}
														>
															<div className="ml-4 pl-4 border-l border-gray-200 space-y-1">
																<InboundAgentCard
																	icon={<PhoneOutlined rotate={0} />}
																	name="AI Receptionist"
																	status="active"
																	className="hover:bg-gray-50"
																/>
																<InboundAgentCard
																	icon={<GlobalOutlined />}
																	name="AI Website Embeddable"
																	status="active"
																	className="hover:bg-gray-50"
																/>
																<InboundAgentCard
																	icon={<MessageOutlined />}
																	name="AI Texting Agent"
																	status="active"
																	className="hover:bg-gray-50"
																/>
															</div>
														</div>
													</div>
												))}

												{/* Display unconfigured inbound agents */}
												{unconfiguredInboundCount > 0 && (
													<>
														<Divider className="my-4" />
														<h3 className="text-lg font-semibold mb-4">Unconfigured Agents</h3>
														{Array.from({ length: unconfiguredInboundCount }).map((_, index) => (
															<div key={`unconfigured-inbound-${index}`} className="mb-4">
																<UnconfiguredAgentCard
																	type="inbound"
																	index={index}
																	onConfigure={() => handleConfigureAgent('inbound')}
																	onDelete={() => handleDeleteUnconfiguredAgent('inbound')}
																	loading={isRemovingInbound}
																	disabled={isRemovingInbound || isRemovingOutbound}
																/>
															</div>
														))}
													</>
												)}

												{loggedInUser.inboundSubscriptionStatus && (
													<>
														<Divider className="my-4" />
														<div className="flex justify-center items-center">
															<Button
																type="primary"
																onClick={() => handleBuyNewAgent('inbound')}
																className="bg-green-600 hover:bg-green-700 w-[250px]"
																icon={<PlusOutlined />}
																size="large"
															>
																Create New Receptionist
															</Button>
														</div>
													</>
												)}
											</>
										)}
									</>
								)}
							</div>
						</Card>

						<Card>
							<div className="p-4 relative">
								{!loggedInUser.accessBetaOutbound && (
									<div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex flex-col items-center justify-center">
										<div className="flex flex-col items-center gap-3">
											<div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
												<LockOutlined className="text-2xl text-blue-600" />
											</div>
											<h3 className="text-xl font-semibold text-gray-800">Coming Soon</h3>
											<p className="text-gray-600 text-center max-w-[200px]">
												Our outbound suite is currently under development. Stay tuned for updates!
											</p>
										</div>
									</div>
								)}
								<div className="flex items-center pb-4 mb-5 border-b border-gray-200">
									<div className="flex items-center justify-center w-9 h-9 rounded-lg text-base mr-4 bg-yellow-50 text-yellow-600">
										<PhoneOutlined />
									</div>
									<h3 className="text-lg font-semibold text-gray-800 m-0">Outbound Suite</h3>
								</div>

								{isAgentsLoading ? (
									<div className="flex justify-center items-center py-8">
										<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600" />
									</div>
								) : (
									<>
										{(loggedInUser.outboundDemo ||
											loggedInUser.outboundSubscriptionStatus === 'canceled') &&
											outboundAgents?.length >= 1 && (
												<div className="bg-blue-50 border border-blue-100 rounded-lg p-4 my-4 flex flex-col items-center">
													<div className="text-blue-700 text-base font-semibold mb-2">
														No active outbound subscription
													</div>
													<div className="text-blue-500 text-sm mb-4 text-center">
														You are currently using a demo account. Please subscribe to unlock all
														outbound agent features!
													</div>
													<Button
														type="primary"
														icon={<PlusOutlined />}
														size="large"
														className="w-full max-w-xs bg-blue-600 hover:bg-blue-700 shadow"
														onClick={() => handleSubscribeNow('outbound')}
													>
														Subscribe Now
													</Button>
												</div>
											)}

										{outboundAgents.length === 0 &&
										(!loggedInUser.outboundSubscriptionId ||
											loggedInUser.outboundSubscriptionStatus === 'canceled') ? (
											<div className="text-center py-8 flex flex-col items-center justify-center">
												<SoundOutlined className="text-4xl text-gray-400 mb-3" />
												<h3 className="text-lg font-medium text-gray-800 mb-2">
													No Outbound Subscription
												</h3>
												<p className="text-gray-600 mb-4">
													Subscribe to Outbound plan to access outbound campaign features
												</p>
												<div className="flex flex-row gap-2 items-center">
													<Button
														type="primary"
														onClick={() => handleSubscribeNow('outbound')}
														className="bg-blue-600 hover:bg-blue-700 "
														icon={<PlusOutlined />}
													>
														Subscribe Now
													</Button>
													{loggedInUser.outboundSubscriptionStatus !== 'canceled' && (
														<Button
															type="default"
															onClick={() => handleCreateDemoAccount('outbound')}
															icon={<UserAddOutlined />}
														>
															Create Demo Account
														</Button>
													)}
												</div>
											</div>
										) : outboundAgents.length === 0 ? (
											<div className="text-center py-8 flex flex-col items-center justify-center">
												<SoundOutlined className="text-4xl text-gray-400 mb-3" />
												<h3 className="text-lg font-medium text-gray-800 mb-2">
													No Outbound Agents
												</h3>
												<p className="text-gray-600 mb-4">
													Create your first outbound agent to get started
												</p>
												<Button
													type="primary"
													onClick={() => handleBuyNewAgent('outbound')}
													className="bg-green-600 hover:bg-green-700 w-[250px]"
													icon={<PlusOutlined />}
													size="large"
												>
													Create New Agent
												</Button>
											</div>
										) : (
											<>
												{outboundAgents.map((agent: User, index: number) => (
													<div key={index} className="mb-4">
														<OutboundAgentCard
															icon={<SoundOutlined />}
															name={agent.name}
															status={
																loggedInUser?.outboundSubscriptionId &&
																loggedInUser?.outboundSubscriptionStatus !== 'canceled'
																	? 'active'
																	: 'demo'
															}
															onExpand={() => {
																const elem = document.getElementById(`outbound-nested-${index}`);
																if (elem) {
																	elem.style.display =
																		elem.style.display === 'none' ? 'block' : 'none';
																}
															}}
															isExpandable={true}
															onOpen={() => {
																dispatch(setSelectedAgentId({ selectedAgentId: agent.number }));
																dispatch(setSelectedAgent({ user: agent }));
																navigate(
																	AppRoutes.OUTBOUND_AGENT_PORTAL.replace(':agentId', agent.number),
																);
															}}
															onDelete={() => handleDeleteAgent(agent, 'outbound')}
															loading={deletingAgentId === agent.number}
															disabled={isRemovingInbound || isRemovingOutbound}
														/>
														<div
															id={`outbound-nested-${index}`}
															className="py-2"
															style={{ display: 'block' }}
														>
															<div className="ml-4 pl-4 border-l border-gray-200">
																<OutboundAgentCard
																	icon={<SoundOutlined />}
																	name="Outbound Campaigns"
																	status="active"
																	className="hover:bg-gray-50"
																/>
															</div>
														</div>
													</div>
												))}

												{/* Display unconfigured outbound agents */}
												{unconfiguredOutboundCount > 0 && (
													<>
														<Divider className="my-4" />
														<h3 className="text-lg font-semibold mb-4">Unconfigured Agents</h3>
														{Array.from({ length: unconfiguredOutboundCount }).map((_, index) => (
															<div key={`unconfigured-outbound-${index}`} className="mb-4">
																<UnconfiguredAgentCard
																	type="outbound"
																	index={index}
																	onConfigure={() => handleConfigureAgent('outbound')}
																	onDelete={() => handleDeleteUnconfiguredAgent('outbound')}
																	loading={isRemovingOutbound}
																	disabled={isRemovingInbound || isRemovingOutbound}
																/>
															</div>
														))}
													</>
												)}

												{loggedInUser.outboundSubscriptionStatus && (
													<>
														<Divider className="my-4" />
														<div className="flex justify-center items-center">
															<Button
																type="primary"
																onClick={() => handleBuyNewAgent('outbound')}
																className="bg-green-600 hover:bg-green-700 w-[250px]"
																icon={<PlusOutlined />}
																size="large"
															>
																Create New Agent
															</Button>
														</div>
													</>
												)}
											</>
										)}
									</>
								)}
							</div>
						</Card>
					</div>
				</div>

				<CsmCard
					avatarUrl={csmMember.imageUrl || ''}
					name={csmMember.name || 'N/A'}
					email={csmMember.email || 'N/A'}
					description="As your Customer Success Manager, I'm here to help you get the most out of our platform. Feel free to book a call with me to discuss any questions or concerns you may have."
					calendlyLink={csmMember.calendlyLink}
				/>
			</div>

			<div>
				<div className="flex justify-between items-center mb-6">
					<div className="flex items-center gap-2">
						<h2 className="text-xl font-bold m-0">Recent Activity</h2>
						<Tooltip title="Only shows the latest 7 days of activity">
							<InfoCircleOutlined className="w-4 h-4" />
						</Tooltip>
					</div>
				</div>

				<Card className="mb-10">
					{recentActivity.length > 0 ? (
						recentActivity.map((activity, index) => (
							<ActivityItem
								key={index}
								type={activity.type}
								title={activity.title}
								subtitle={activity.subtitle}
								time={activity.time}
								borderBottom={index < recentActivity.length - 1}
							/>
						))
					) : (
						<div className="p-5 text-center text-gray-500">No recent activity</div>
					)}
				</Card>
			</div>
			<InboundPlansModal />
			<OutboundPlansModal />
			{isCampaignCreationModalOpen && (
				<CampaignCreationModal
					isOpen={isCampaignCreationModalOpen}
					setShowCreateCampaign={setIsCampaignCreationModalOpen}
					onClose={() => setIsCampaignCreationModalOpen(false)}
					refetch={() => {
						refetchAgents();
						refetchUser();
					}}
					handleShowCampaign={(id: string) => {
						dispatch(setSelectedAgentId({ selectedAgentId: id }));
						dispatch(
							setSelectedAgent({
								user: outboundAgents.find((agent) => agent.number === id) as User,
							}),
						);
						navigate(AppRoutes.OUTBOUND_AGENT_PORTAL.replace(':agentId', id));
					}}
				/>
			)}
			{isInboundAgentCreationModalOpen && (
				<InboundAgentCreationModal
					isOpen={isInboundAgentCreationModalOpen}
					onClose={() => setIsInboundAgentCreationModalOpen(false)}
					setShowCreateAgent={setIsInboundAgentCreationModalOpen}
					refetch={() => {
						refetchAgents();
						refetchUser();
					}}
				/>
			)}

			<AddAgentSubscriptionModal
				visible={!!addAgentModalOpen}
				onCancel={() => setAddAgentModalOpen(null)}
				onContinue={() => {
					if (addAgentModalOpen === 'inbound') {
						setIsInboundAgentCreationModalOpen(true);
					} else if (addAgentModalOpen === 'outbound') {
						setIsCampaignCreationModalOpen(true);
					}
					setAddAgentModalOpen(null);
				}}
				agentType={addAgentModalOpen as 'inbound' | 'outbound'}
			/>

			<UnconfiguredAgentModal
				visible={!!unconfiguredAgentModalOpen}
				onCancel={() => setUnconfiguredAgentModalOpen(null)}
				onContinue={() => {
					if (unconfiguredAgentModalOpen === 'inbound') {
						setIsInboundAgentCreationModalOpen(true);
					} else if (unconfiguredAgentModalOpen === 'outbound') {
						setIsCampaignCreationModalOpen(true);
					}
					setUnconfiguredAgentModalOpen(null);
				}}
				agentType={unconfiguredAgentModalOpen as 'inbound' | 'outbound'}
				unconfiguredCount={
					unconfiguredAgentModalOpen === 'inbound'
						? loggedInUser?.unConfiguredInboundAgents || 0
						: loggedInUser?.unConfiguredOutboundAgents || 0
				}
			/>
		</div>
	);
};

export default Dashboard;
